import { useCallback, useEffect, useState } from "react";

interface UseLogViewerOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseLogViewerResult {
  logs: string;
  loading: boolean;
  error: string | null;
  setLogs: (logs: string) => void;
  refetch: () => Promise<void>;
}

/**
 * `В4` (условие слияния `F-454`): просмотр и скачивание журнала.
 *
 * **Очистки здесь нет.** Прежде страницы трафика стирали журнал (`DELETE /logs`),
 * и нажатие кнопки на них стирало `app.log` **бэкенда** — то есть технический
 * журнал сервера, а не файл шлюза, который человек думал, что чистит. Теперь
 * очистка одна и живёт на `/admin/devlog` (`POST /admin/logs/clear`, со следом
 * `system_log.cleared` в журнале действий); страницы трафика только показывают и
 * скачивают.
 */
export function useLogViewer(
  fetchLogs: () => Promise<string>,
  options: UseLogViewerOptions = {},
): UseLogViewerResult {
  const { autoRefresh = true, refreshInterval = 5000 } = options;

  const [logs, setLogs] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      const logsText = await fetchLogs();
      setLogs(logsText);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Ошибка загрузки логов");
    } finally {
      setLoading(false);
    }
  }, [fetchLogs]);

  useEffect(() => {
    fetch();

    let interval: ReturnType<typeof setInterval> | undefined;
    if (autoRefresh) {
      interval = setInterval(fetch, refreshInterval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetch, autoRefresh, refreshInterval]);

  return {
    logs,
    loading,
    error,
    setLogs,
    refetch: fetch,
  };
}
